import { Component, EventEmitter, Input, OnChanges, Output, computed, inject, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import type { Producto } from '../../domain/entidades'
import { completarFichaProducto, actualizarNombreProveedorDashboard } from '../../application/casos-de-uso'
import { AuthService } from '../../infrastructure/auth/auth.service'
import { puedeCompletarFichaDinamico } from '../../infrastructure/permisos/cliente'
import { VisorImagenComponent } from './visor-imagen'
import { FotoRemotaComponent } from './foto-remota'
import { CampoFotoDashboardComponent } from './campo-foto-dashboard'
import { ConfirmarBorradoModalComponent } from './confirmar-borrado-modal'
import { numeroDesdeTexto, esNumeroDesdeTexto } from '../../infrastructure/util/numero'
import type { Tela } from '../../domain/entidades'
import type { SlotFotoDashboard } from '../../infrastructure/storage/reemplazo-fotos-dashboard'
import {
  eliminarProductoDashboard,
  eliminarProveedorDashboard,
  eliminarTelaDashboard,
} from '../../infrastructure/supabase/eliminar-desde-dashboard'

const cop = (n: number) => n.toLocaleString('es-CO')

interface FotoDeGrupo {
  url: string
  titulo: string
  kind?: 'frente' | 'reverso'
}

/** Panel de detalle para revisar telas y completar la ficha comercial del producto. */
@Component({
  selector: 'app-detalle-panel',
  standalone: true,
  imports: [FormsModule, VisorImagenComponent, FotoRemotaComponent, CampoFotoDashboardComponent, ConfirmarBorradoModalComponent],
  templateUrl: './detalle-panel.html',
  styleUrl: './detalle-panel.scss',
})
export class DetallePanelComponent implements OnChanges {
  private readonly auth = inject(AuthService)

  @Input({ required: true }) producto!: Producto
  @Input({ required: true }) proveedorNombre = ''
  @Input() proveedorFotoTarjetaUrl?: string
  @Output() guardado = new EventEmitter<void>()
  /** Emitido tras borrar el producto o el proveedor completo — el padre debe dejar de mostrar este panel. */
  @Output() eliminado = new EventEmitter<void>()

  readonly nombreComercial = signal('')
  readonly notas = signal('')
  readonly precioVentaCol = signal('')
  readonly precioVentaPan = signal('')
  readonly categoria = signal('')
  readonly linea = signal('')
  readonly sublinea = signal('')
  readonly unidadesPorContenedor = signal('')
  readonly guardando = signal(false)
  readonly mostrarToast = signal(false)

  /** Edición de nombre de proveedor desde el dashboard — pedido desde la feria, 2026-09-05. */
  readonly editandoNombreProveedor = signal(false)
  readonly nombreProveedorEditado = signal('')
  readonly guardandoNombreProveedor = signal(false)

  private ultimoProductoId?: string

  get puedeEditar(): boolean {
    const perfil = this.auth.perfil()
    return perfil ? puedeCompletarFichaDinamico(perfil.rol) : false
  }

  get rolActual(): string {
    return this.auth.perfil()?.rol ?? ''
  }

  get primeraTela() {
    return this.producto.telas[0]
  }

  get coloresTelaFondo(): string[] {
    return [this.primeraTela?.colorFrente, this.primeraTela?.colorReverso].filter((c): c is string => Boolean(c))
  }

  /**
   * Navegación con flechas dentro del visor — pedido desde la feria
   * (2026-09-05): cada grupo de fotos es independiente. "Fotos del
   * producto" navega solo entre sí; cada tela navega solo entre sus propias
   * 2 fotos (frente/reverso), nunca mezclado con otra tela ni con las fotos
   * del producto. El grupo activo (`grupoImagenes`) y el índice dentro de
   * ese grupo son la única fuente de verdad — `imagenAmpliada` se deriva.
   */
  private readonly grupoImagenes = signal<FotoDeGrupo[]>([])
  private readonly indiceImagenActual = signal(0)
  readonly imagenAmpliada = computed(() => this.grupoImagenes()[this.indiceImagenActual()] ?? null)
  readonly puedeImagenAnterior = computed(() => this.indiceImagenActual() > 0)
  readonly puedeImagenSiguiente = computed(() => this.indiceImagenActual() < this.grupoImagenes().length - 1)

  /** Fotos del producto (galería) — grupo precalculado en ngOnChanges. */
  grupoFotosProducto: FotoDeGrupo[] = []
  /** Las 2 fotos (frente/reverso) de cada tela, por id de tela — nunca se mezclan entre telas. */
  private gruposPorTela = new Map<string, FotoDeGrupo[]>()

  private abrirGrupo(grupo: FotoDeGrupo[], indice: number): void {
    if (grupo.length === 0 || indice < 0 || indice >= grupo.length) return
    this.grupoImagenes.set(grupo)
    this.indiceImagenActual.set(indice)
  }

  abrirEdicionNombreProveedor(): void {
    this.nombreProveedorEditado.set(this.proveedorNombre)
    this.editandoNombreProveedor.set(true)
  }

  cerrarEdicionNombreProveedor(): void {
    this.editandoNombreProveedor.set(false)
  }

  async guardarNombreProveedor(): Promise<void> {
    this.guardandoNombreProveedor.set(true)
    try {
      await actualizarNombreProveedorDashboard(this.producto.proveedorCodigo, this.nombreProveedorEditado().trim())
      this.editandoNombreProveedor.set(false)
      this.guardado.emit()
    } finally {
      this.guardandoNombreProveedor.set(false)
    }
  }

  /** Slots de foto editables desde el dashboard (pedido de Angie, 2026-09-16) — un único componente reusable por campo. */
  get slotTarjetaProveedor(): SlotFotoDashboard {
    return {
      tabla: 'proveedores',
      columnaId: 'codigo',
      filaId: this.producto.proveedorCodigo,
      columna: 'foto_tarjeta_url',
      rutaBase: `${this.producto.proveedorCodigo}/tarjeta`,
    }
  }

  get slotColorEstructura(): SlotFotoDashboard {
    return {
      tabla: 'productos',
      columnaId: 'id',
      filaId: this.producto.id,
      columna: 'color_estructura_foto_url',
      rutaBase: `${this.producto.id}/color-estructura`,
    }
  }

  slotTelaFrente(t: Tela): SlotFotoDashboard {
    return { tabla: 'telas', columnaId: 'id', filaId: t.id, columna: 'foto_frente_url', rutaBase: `${t.productoId}/${t.id}-frente` }
  }

  slotTelaReverso(t: Tela): SlotFotoDashboard {
    return { tabla: 'telas', columnaId: 'id', filaId: t.id, columna: 'foto_reverso_url', rutaBase: `${t.productoId}/${t.id}-reverso` }
  }

  /** Mismo patrón que guardarNombreProveedor(): el dashboard no tiene realtime para "proveedores"/cambios puntuales de fotos, así que forzamos un recargar() explícito en el padre. */
  alActualizarFotoDashboard(): void {
    this.guardado.emit()
  }

  // ---------- Borrado (pedido de Angie, 2026-09-16) ----------
  // Confirmación reforzada: hay que escribir el código exacto para habilitar
  // el botón — ver ConfirmarBorradoModalComponent.

  readonly mostrarBorrarProducto = signal(false)
  readonly mostrarBorrarProveedor = signal(false)
  readonly telaABorrar = signal<Tela | null>(null)

  readonly accionBorrarProducto = () => eliminarProductoDashboard(this.producto.id)
  readonly accionBorrarProveedor = () => eliminarProveedorDashboard(this.producto.proveedorCodigo)
  accionBorrarTela(t: Tela): () => Promise<void> {
    return () => eliminarTelaDashboard(t.id)
  }

  alEliminarProducto(): void {
    this.mostrarBorrarProducto.set(false)
    this.eliminado.emit()
  }

  alEliminarProveedor(): void {
    this.mostrarBorrarProveedor.set(false)
    this.eliminado.emit()
  }

  alEliminarTela(): void {
    this.telaABorrar.set(null)
    this.guardado.emit()
  }

  abrirTarjetaProveedor(): void {
    if (!this.proveedorFotoTarjetaUrl) return
    this.abrirGrupo([{ url: this.proveedorFotoTarjetaUrl, titulo: 'Tarjeta · ' + this.proveedorNombre }], 0)
  }

  abrirFotoProducto(indice: number): void {
    this.abrirGrupo(this.grupoFotosProducto, indice)
  }

  abrirFotoTela(t: Tela, cual: 'frente' | 'reverso'): void {
    const grupo = this.gruposPorTela.get(t.id) ?? []
    const indice = grupo.findIndex((f) => f.kind === cual)
    if (indice === -1) return
    this.abrirGrupo(grupo, indice)
  }

  imagenAnterior(): void {
    if (this.puedeImagenAnterior()) this.indiceImagenActual.update((i) => i - 1)
  }

  imagenSiguiente(): void {
    if (this.puedeImagenSiguiente()) this.indiceImagenActual.update((i) => i + 1)
  }

  cerrarImagen(): void {
    this.grupoImagenes.set([])
    this.indiceImagenActual.set(0)
  }

  private armarGruposDeFotos(): void {
    this.grupoFotosProducto = (this.producto.fotos ?? []).map((f) => ({
      url: f.url,
      titulo: f.etiqueta || this.producto.mueble,
    }))

    this.gruposPorTela = new Map(
      this.producto.telas.map((t) => {
        const items: FotoDeGrupo[] = []
        if (t.fotoFrenteUrl) items.push({ url: t.fotoFrenteUrl, titulo: `${t.colores || 'Tela'} · Frente`, kind: 'frente' })
        if (t.fotoReversoUrl) items.push({ url: t.fotoReversoUrl, titulo: `${t.colores || 'Tela'} · Reverso`, kind: 'reverso' })
        return [t.id, items] as const
      }),
    )
  }

  ngOnChanges(): void {
    // Los grupos de fotos se reconstruyen SIEMPRE, incluso si el producto
    // sigue siendo el mismo (ej. tras reemplazar una foto desde el
    // dashboard, `recargar()` trae el mismo id con una URL nueva) — a
    // diferencia de los campos de texto de abajo, acá no hay nada que
    // "perder" por refrescar de más. Sin esto, el visor ampliado seguía
    // mostrando la foto reemplazada porque `gruposPorTela` solo se armaba la
    // primera vez que se abría este producto.
    this.armarGruposDeFotos()

    if (this.producto.id === this.ultimoProductoId) return
    this.ultimoProductoId = this.producto.id
    this.nombreComercial.set(this.producto.nombreComercial)
    this.notas.set(this.producto.notas)
    this.precioVentaCol.set(this.producto.precioVentaCol?.toString() ?? '')
    this.precioVentaPan.set(this.producto.precioVentaPan?.toString() ?? '')
    this.categoria.set(this.producto.categoria ?? '')
    this.linea.set(this.producto.linea ?? '')
    this.sublinea.set(this.producto.sublinea ?? '')
    this.unidadesPorContenedor.set(this.producto.unidadesPorContenedor?.toString() ?? '')
  }

  formatoCop(valor: string): string | null {
    const numero = numeroDesdeTexto(valor)
    return numero === undefined ? null : cop(numero)
  }

  esNumeroValido(valor: string): boolean {
    return esNumeroDesdeTexto(valor)
  }

  async guardar(): Promise<void> {
    this.guardando.set(true)
    try {
      await completarFichaProducto(this.producto.id, {
        nombreComercial: this.nombreComercial(),
        notas: this.notas(),
        precioVentaCol: numeroDesdeTexto(this.precioVentaCol()),
        precioVentaPan: numeroDesdeTexto(this.precioVentaPan()),
        categoria: this.categoria(),
        linea: this.linea(),
        sublinea: this.sublinea(),
        unidadesPorContenedor: numeroDesdeTexto(this.unidadesPorContenedor()),
      })
      this.mostrarToast.set(true)
      setTimeout(() => this.mostrarToast.set(false), 1800)
      this.guardado.emit()
    } finally {
      this.guardando.set(false)
    }
  }
}
